import axios, { AxiosInstance } from 'axios'
import type {
  DashboardMetrics,
  PodInfo,
  AdminStreamResource,
  InfrastructureHealth,
  TeamResource,
  OrgResource,
  AdminOrganizationUpdatePayload,
  WaitlistEntryResource,
  SrtBridgeResource,
} from '@/types/admin'
import type { TokenResource } from '@/types/tokens'
import type { ProfileUpdate, UserResource } from '@/types/users'
import type {
  CreateFeedbackItemPayload,
  FeedbackCommentsResponse,
  FeedbackFilters,
  FeedbackItemResponse,
  FeedbackItemsResponse,
} from '@/types/feedback'

class ApiClient {
  private client: AxiosInstance
  private authToken: string | null = null

  constructor() {
    this.authToken = this.readStoredToken()

    this.client = axios.create({
      baseURL: import.meta.env.VITE_API_BASE_URL || 'http://localhost:3000/api/v1',
      headers: { 'Content-Type': 'application/json' },
    })

    this.client.interceptors.request.use((config) => {
      const token = this.authToken || this.readStoredToken()

      if (token) {
        config.headers = config.headers || {}
        config.headers['Authorization'] = `Bearer ${token}`
      }
      return config
    })
  }

  private readStoredToken() {
    if (typeof window === 'undefined') return null

    return window.localStorage.getItem('sb_token')
  }

  setToken(token: string | null) {
    this.authToken = token
  }

  get<T>(url: string, params?: Record<string, unknown>) {
    return this.client.get<T>(url, { params }).then((r) => r.data)
  }
  post<T>(url: string, data?: unknown) {
    return this.client.post<T>(url, data).then((r) => r.data)
  }
  patch<T>(url: string, data?: unknown) {
    return this.client.patch<T>(url, data).then((r) => r.data)
  }
  put<T>(url: string, data?: unknown) {
    return this.client.put<T>(url, data).then((r) => r.data)
  }
  delete<T>(url: string) {
    return this.client.delete<T>(url).then((r) => r.data)
  }
  /** A file the API answers, such as a CSV export, as a Blob. */
  getBlob(url: string) {
    return this.client.get<Blob>(url, { responseType: 'blob' }).then((r) => r.data)
  }

  // Multipart helpers — used for endpoints that accept file uploads alongside
  // JSON fields (e.g. campaign creatives). Axios sets the multipart boundary
  // when given FormData; the explicit content-type is removed so axios can
  // pick the right boundary value.
  postMultipart<T>(url: string, data: FormData) {
    return this.client.post<T>(url, data, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
  }
  patchMultipart<T>(url: string, data: FormData) {
    return this.client.patch<T>(url, data, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
  }
  putMultipart<T>(url: string, data: FormData) {
    return this.client.put<T>(url, data, { headers: { 'Content-Type': 'multipart/form-data' } }).then((r) => r.data)
  }


  // Stream metrics
  getStreamMetrics(streamId: string, params?: { from?: string; to?: string; aggregate?: string; limit?: number }) {
    return this.get(`/streams/${streamId}/metrics`, params)
  }
  
  // Streams
  getStreams(params?: { event_id?: string; status?: string }) {
    return this.get(`/streams`, params)
  }
  
  // Stream control
  startStream(streamId: string) {
    return this.post(`/streams/${streamId}/start`)
  }

  // Admin API
  getAdminHealth(eventId?: string, organizationId?: string) {
    const params: Record<string, string> = {}
    if (eventId) params.event_id = eventId
    if (organizationId) params.organization_id = organizationId
    return this.get<{ data: InfrastructureHealth }>('/admin/health', Object.keys(params).length ? params : undefined)
  }

  getAdminDashboard() {
    return this.get<{ data: DashboardMetrics }>('/admin/dashboard')
  }

  getAdminPods(params?: { status?: string; platform?: string }) {
    return this.get<{ data: PodInfo[] }>('/admin/pods', params)
  }

  getAdminPod(podName: string) {
    return this.get<{ data: PodInfo }>(`/admin/pods/${podName}`)
  }

  destroyAdminPod(podName: string) {
    return this.delete(`/admin/pods/${podName}`)
  }

  cleanupAdminPods(dryRun: boolean = false) {
    return this.post('/admin/pods/cleanup', { dry_run: dryRun })
  }

  getAdminStreams(params?: { status?: string; organization_id?: string }) {
    return this.get<{ data: AdminStreamResource[] }>('/admin/streams', params)
  }

  getAdminSrtBridges() {
    return this.get<{ data: SrtBridgeResource[] }>('/admin/srt_bridges')
  }

  getAdminSrtBridge(bridgeId: string) {
    return this.get<{ data: SrtBridgeResource }>(`/admin/srt_bridges/${bridgeId}`)
  }

  createAdminSrtBridge(data: { name: string; source_url: string; destination_url: string; enabled: boolean }) {
    return this.post<{ data: SrtBridgeResource }>('/admin/srt_bridges', { srt_bridge: data })
  }

  updateAdminSrtBridge(bridgeId: string, data: Partial<{ name: string; source_url: string; destination_url: string; enabled: boolean }>) {
    return this.patch<{ data: SrtBridgeResource }>(`/admin/srt_bridges/${bridgeId}`, { srt_bridge: data })
  }

  enableAdminSrtBridge(bridgeId: string) {
    return this.post<{ data: SrtBridgeResource }>(`/admin/srt_bridges/${bridgeId}/enable`)
  }

  disableAdminSrtBridge(bridgeId: string) {
    return this.post<{ data: SrtBridgeResource }>(`/admin/srt_bridges/${bridgeId}/disable`)
  }

  deleteAdminSrtBridge(bridgeId: string) {
    return this.delete(`/admin/srt_bridges/${bridgeId}`)
  }

  getAdminOrganizations() {
    return this.get<{ data: OrgResource[] }>('/admin/organizations')
  }

  getAdminOrganization(organizationId: string) {
    return this.get<{ data: OrgResource }>(`/admin/organizations/${organizationId}`)
  }

  updateAdminOrganization(organizationId: string, data: AdminOrganizationUpdatePayload) {
    return this.patch<{ data: OrgResource }>(`/admin/organizations/${organizationId}`, { organization: data })
  }

  getAdminTeams(params?: { organization_id?: string }) {
    return this.get<{ data: TeamResource[] }>('/admin/teams', params)
  }

  createAdminTeam(data: { name: string; description?: string; organization_id?: string }) {
    return this.post('/admin/teams', { team: data })
  }

  updateAdminTeam(teamId: string, data: { name?: string; description?: string; active?: boolean }) {
    return this.patch(`/admin/teams/${teamId}`, { team: data })
  }

  deleteAdminTeam(teamId: string) {
    return this.delete(`/admin/teams/${teamId}`)
  }

  getAdminUsers(params?: { organization_id?: string; team_id?: string; role?: string }) {
    return this.get('/admin/users', params)
  }

  updateAdminUser(userId: string, data: { role?: string; team_id?: string | null }) {
    return this.patch(`/admin/users/${userId}`, { user: data })
  }

  joinPrivateBetaWaitlist(data: { email: string; main_use_case: string }) {
    return this.post<{ data: { message: string } }>('/private_beta/waitlist_entries', { waitlist_entry: data })
  }

  getPrivateBetaInviteCodeValidity(inviteCode: string) {
    return this.get<{ data: { valid: boolean; reason?: string } }>('/auth/invite_code', { invite_code: inviteCode })
  }

  getAdminPrivateBetaWaitlistEntries(params?: { status?: string }) {
    return this.get<{ data: WaitlistEntryResource[] }>('/admin/private_beta_waitlist_entries', params)
  }

  sendAdminPrivateBetaInvite(waitlistEntryId: string) {
    return this.post(`/admin/private_beta_waitlist_entries/${waitlistEntryId}/invite`)
  }

  // API Tokens (Personal Access Tokens)
  getTokens() {
    return this.get<{ data: TokenResource[] }>('/auth/tokens')
  }

  createToken(data: { name: string; expires_at?: string }) {
    return this.post<{ data: TokenResource }>('/auth/tokens', { token: data })
  }

  revokeToken(tokenId: string) {
    return this.delete(`/auth/tokens/${tokenId}`)
  }

  // Profile: the signed-in user's own name, username, photo and password
  updateProfile(user: ProfileUpdate) {
    return this.patch<{ data: UserResource }>('/profile', { user })
  }

  uploadAvatar(photo: Blob, filename: string) {
    const data = new FormData()
    data.append('avatar', photo, filename)
    return this.patchMultipart<{ data: UserResource }>('/profile/avatar', data)
  }

  removeAvatar() {
    return this.delete<{ data: UserResource }>('/profile/avatar')
  }

  changePassword(currentPassword: string, newPassword: string) {
    return this.post('/auth/change-password', { user: { current_password: currentPassword, new_password: newPassword } })
  }

  listFeedbackItems(params?: FeedbackFilters) {
    return this.get<FeedbackItemsResponse>('/feedback_items', params as Record<string, unknown> | undefined)
  }

  getFeedbackItem(feedbackItemId: string) {
    return this.get<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}`)
  }

  getFeedbackRoadmap() {
    return this.get<FeedbackItemsResponse>('/feedback_items/roadmap')
  }

  createFeedbackItem(data: CreateFeedbackItemPayload) {
    return this.post<FeedbackItemResponse>('/feedback_items', { feedback_item: data })
  }

  updateFeedbackItem(feedbackItemId: string, data: Partial<CreateFeedbackItemPayload>) {
    return this.patch<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}`, { feedback_item: data })
  }

  changeFeedbackStatus(feedbackItemId: string, status: string, statusReason?: string) {
    return this.patch<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/status`, {
      feedback_item: {
        status,
        status_reason: statusReason,
      },
    })
  }

  changeFeedbackPriority(feedbackItemId: string, priority: string) {
    return this.patch<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/priority`, {
      feedback_item: { priority },
    })
  }

  createFeedbackComment(feedbackItemId: string, body_markdown: string) {
    return this.post(`/feedback_items/${feedbackItemId}/comments`, {
      feedback_comment: { body_markdown },
    })
  }

  listFeedbackComments(feedbackItemId: string) {
    return this.get<FeedbackCommentsResponse>(`/feedback_items/${feedbackItemId}/comments`)
  }

  voteFeedbackItem(feedbackItemId: string) {
    return this.post<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/vote`)
  }

  unvoteFeedbackItem(feedbackItemId: string) {
    return this.delete<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/vote`)
  }

  followFeedbackItem(feedbackItemId: string) {
    return this.post<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/follow`)
  }

  unfollowFeedbackItem(feedbackItemId: string) {
    return this.delete<FeedbackItemResponse>(`/feedback_items/${feedbackItemId}/follow`)
  }
}

export const api = new ApiClient()
